import { useEffect, useState } from 'react';
import { api } from '../api';
import '../styles/privacy.css';
import '../styles/extension-changelog.css';

interface ChangelogEntry {
  version: string;
  date: string;
  skipped?: boolean;
  sections: {
    heading?: string;
    items: string[];
  }[];
}

// Fallback when CWS API is not configured — update when submitting a new version
const FALLBACK_STORE_VERSION = '1.5.0';
const FALLBACK_STORE_STATUS = 'DRAFT';

function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const na = pa[i] || 0;
    const nb = pb[i] || 0;
    if (na !== nb) return na - nb;
  }
  return 0;
}

const changelog: ChangelogEntry[] = [
  {
    version: '1.5.0',
    date: 'Mar 23, 2026',
    sections: [
      {
        heading: 'Validation Stats & Insights',
        items: [
          'Session stats bar — The popup now shows a compact stats bar with counts of flows validated, issues found, issues fixed, and clean flows. Stats reset when captures are cleared.',
          'Flow diff awareness — When the same flow is validated again, warnings are classified as "new", "resolved", or "persistent". New warnings show a blue NEW badge in the popup, side panel report, and page overlay. Resolved warnings are counted with a green badge.',
          'Rule changelog in extension — Server-side rule updates are now surfaced in the Options page under a new "Rule Updates" section. A blue dot appears on the popup changelog icon when there are unseen updates.'
        ]
      },
      {
        heading: 'Batch Validation',
        items: [
          'Validate tab in side panel — A new "Validate" tab lets you paste or drag-drop flow JSON and run validation without being on the IO platform. Results display in read-only mode (no fix buttons) using the same report format.',
          'No marketplace connection required — Batch validation works offline using the extension\'s synced rules.'
        ]
      },
      {
        heading: 'Report Export',
        items: [
          'Copy report — The side panel report view now has a "Copy" button that copies the validation report as markdown to the clipboard, ready for pasting into tickets or docs.',
          'Download report — The "Download" button exports the report as a structured JSON file for archiving or automated processing.'
        ]
      },
      {
        heading: 'Flow Health Dashboard (Opt-in)',
        items: [
          'Anonymized health reporting — Enable "Share anonymized validation statistics" in Settings to send rule IDs and severity counts (no flow content) to the IO Marketplace. Data helps identify common issues across teams.',
          'Admin dashboard — The marketplace admin dashboard now includes a Flow Health section with charts showing most common validation issues, issues over time, and per-domain breakdowns.',
          'Privacy-first — Flow IDs are hashed, no flow content is sent, and reporting is off by default. Data expires after 90 days.'
        ]
      }
    ]
  },
  {
    version: '1.4.0',
    date: 'Mar 16, 2026',
    sections: [
      {
        heading: 'Capture & Popup Improvements',
        items: [
          'Flow name display — The popup and side panel now show the flow name instead of the raw API URL. Hover tooltip shows both name and URL for full context.',
          'Per-tab filtering — The popup only shows captures from the currently active browser tab, so switching between flows shows only relevant results.',
          'Capture deduplication — Same save intercepted via both fetch and XMLHttpRequest no longer creates duplicate entries in the capture list (2-second window dedup).'
        ]
      },
      {
        heading: 'Fix Reliability',
        items: [
          'Full header preservation — All original request headers are now captured during interception and replayed when applying fixes or reverting. This prevents webhook and API flows from breaking due to missing custom headers (e.g. flow-type-specific headers the IO platform requires).',
          'Webhook/API flow fix support — The dangling-output fix now detects webhook and API flows (via flow.type and start_be node presence) and creates the correct backend nodes (log_be + end_failure_be) instead of frontend variants that the IO platform would reject.'
        ]
      },
      {
        heading: 'Side Panel Report',
        items: [
          'Hidden warnings section — The side panel report now shows muted and dismissed warnings in a collapsible section at the bottom, with individual "Show" buttons to restore them. Matches the overlay\'s hidden warnings functionality.',
          'Mute rule from report — Individual warning items in the report view now have a "Mute rule" button, not just the group headers.',
          'Flow name in report — Report header and subtitle display the flow name with URL tooltip, consistent with the popup display.'
        ]
      },
      {
        heading: 'Nested Flow Improvements',
        items: [
          'Recursive nested flow resolution — The extension now fetches nested flows up to 10 levels deep (up from 1), so variables set by deeply nested library flows are correctly recognized. Cycle protection ensures flows that reference each other don\'t cause infinite loops.',
          'Recursive variable resolution — The undefined-variable rule now checks whether a nested_flow node\'s resolved library sets a variable, eliminating false positives for multi-level nesting chains.'
        ]
      },
      {
        heading: 'Dynamic Metadata from IO Platform (IO 5.3+)',
        items: [
          'Node metadata caching — The extension now intercepts NodeMetadatas, NodeCategories, and FlowTypes API responses from the IO platform and caches them per domain. Metadata is used by validation rules for smarter, context-aware checks.',
          'Metadata sync to marketplace — Cached metadata is automatically synced to the IO Marketplace server, enabling server-side tools to inspect available node types for debugging and investigation.',
          'Metadata-aware start-missing-output — Instead of hardcoding "Success"/"Failure", the rule reads expected outputs from node metadata by level (success/error). Flow types that override start node outputs (e.g. invite_user with Authorized/NotAuthenticated/NotAuthorized) are handled gracefully.',
          'New rule: deprecated-node — Flags nodes using types marked as deprecated in the platform metadata, showing the display name and suggesting the newer version. Silently skips when no metadata is available (pre-5.3).',
          'Total validation rules: 30 (up from 28).'
        ]
      },
      {
        heading: 'Fix Infrastructure',
        items: [
          'Warning message passed to fix functions — Fix functions now receive the warning message text, enabling context-aware fixes that know exactly which issue triggered the warning.',
          'Declare as Input for nested flow variables — The nested-flow-missing-variable rule now offers a "Declare as Input" button. In library flows, clicking it adds the required variable to the nested_start comment, documenting the chaining requirement for callers.',
          'Simplified missing-end-fields rule — Only flags backend end nodes missing an HTTP status code. Body and headers are optional and no longer flagged.',
          'New rule: nested-flow-unconfigured — Flags nested_flow nodes with no library flow assigned.',
          'Add Missing Output fix for nested flows — When a nested_flow node calls a library with custom end nodes not configured in decision_outputs, the "Add Missing Output" button adds the output to decision_outputs, decision_outputs_order, and the node\'s outputs. Also supports bulk "Add All Missing Outputs".',
          'Dead path detection for nested flows — Warns when NestedSuccess or NestedFailure outputs are connected but the library flow has no matching end node. Also warns when custom decision_outputs are connected but have no corresponding library end. These paths will never execute at runtime.'
        ]
      }
    ]
  },
  {
    version: '1.3.0',
    date: 'Mar 10, 2026',
    sections: [
      {
        heading: 'Architecture: Fully Externalized Validation Rules',
        items: [
          'Zero hardcoded rules — All 28 validation rules (check, fix, fixAll, canFix) now live on the IO Marketplace as JavaScript code strings. The extension fetches, compiles, and executes them at runtime. Rule logic changes no longer require a Chrome Web Store release.',
          'Sandbox-based execution — Rule code is compiled and executed in a sandboxed iframe (MV3 CSP compliant) via an offscreen document bridge. This keeps the service worker secure while allowing dynamic code compilation in the sandbox.',
          'Instant rule updates — Edit a rule\'s check/fix logic, severity, description, or enabled state in the admin portal — the extension picks it up on next sync (hourly auto-refresh or manual "Sync Now"). No CWS review wait.',
          'Add new rules remotely — Create entirely new handler rules from the admin portal with custom JavaScript check/fix logic. The extension will compile and run them without any code changes.',
          'Fully self-contained rules — All helper functions are inlined into each rule. Zero dependency on extension-side code — every rule can be updated or fixed remotely.',
          'Auto-seed on deploy — Rules automatically seed into the database on server startup. No manual step needed.',
          'Per-rule fingerprints — Each rule gets a hash of its code, visible in the Options debug panel for version troubleshooting.',
          'IO 5.3 compatibility — All rules check both node.data and node.meta for properties.'
        ]
      },
      {
        heading: 'Admin Portal',
        items: [
          'Code editors for handler rules — Admin Validator Config page now shows editable checkBody, fixBody, fixAllBody, and canFixBody textareas for each handler rule.',
          'Add/remove handler rules — "Add Handler Rule" button and per-rule remove buttons for full rule management.'
        ]
      },
      {
        heading: 'Library Flow Support',
        items: [
          'Declared library inputs — Wrap the nested_start node in a comment containing {{variable}} references to declare expected parent-flow inputs. The undefined-variable rule reads these and suppresses false positives.',
          'Auto-fix for library inputs — Click "Declare as Input" on an undefined-variable warning in a library flow to automatically add the variable to the nested_start comment. Creates the comment if one doesn\'t exist.',
          'Custom fix button labels — Rules can now specify custom button text (e.g. "Declare as Input" instead of "Fix") to better describe what the action does. Progress states reflect the custom label.'
        ]
      },
      {
        heading: 'Debug & Diagnostics',
        items: [
          'Debug panel in Options — Collapsible "Debug Info" section under Rule Sync shows sandbox compilation results, per-rule hash fingerprints, and version timestamp for troubleshooting.',
          'Rule fingerprints in rule list — Each rule in the Validation Rules section now displays its hash fingerprint, making it easy to verify which version of the rule code is active.'
        ]
      },
      {
        heading: 'Bug Fixes',
        items: [
          'Fixed sandbox rule loss — Chrome can kill offscreen documents to save memory, which caused the sandbox to lose all compiled rules. Validation now automatically re-compiles rules when the sandbox is recreated, ensuring rules are never silently dropped.',
          'Fixed closure-over-var in sandbox compilation — All fix/fixAll functions shared the same closure variable (var instead of let), causing every rule\'s Fix button to execute the last-compiled rule\'s fixBody. Changed to let for proper block scoping.',
          'Fixed canFixBody crash — The canFix function receives only the warning object (not the flow body), but the undefined-variable canFixBody referenced body, crashing all validation for all rules. Fixed to return true.'
        ]
      },
      {
        items: [
          'Note: v1.2.0 was not separately published to the Chrome Web Store — all of its features are included in v1.3.0. Since v1.3.0 supersedes it with the externalized rules architecture, we skipped the v1.2.0 CWS submission to avoid an unnecessary review cycle.'
        ]
      }
    ]
  },
  {
    version: '1.2.0',
    date: 'Mar 10, 2026',
    skipped: true,
    sections: [
      {
        heading: 'New Features',
        items: [
          'Flow browsing in side panel — Browse flows alongside nodes with a Nodes/Flows toggle. Each card shows a kind badge (purple for Node, blue for Flow).',
          'Flow copy & download — For flows, "Copy" extracts the node list in a paste-ready format for the IO Designer. "Download" saves the full flow JSON file.',
          'Kind persistence — Selected Nodes/Flows tab persists across side panel reopens.'
        ]
      },
      {
        heading: 'Validation Improvements',
        items: [
          'Malformed template variable detection — New rule detects mismatched brackets around template variables (e.g. {x.y}} or {{x.y} instead of {{x.y}}), and flags bare namespace.key references that match a known flow variable but are missing {{ }} entirely.',
          'Remote validator config — The extension now syncs rule overrides and pattern rules from the IO Marketplace server. Admins can disable rules, change severity, or add regex-based pattern rules remotely without a new extension release. Config is cached locally and refreshed hourly.',
          'Rule sync status — Options page shows sync status (source, rule counts, config version, last check, errors) with a "Sync Now" button for immediate refresh.'
        ]
      },
      {
        heading: 'Auto-Fix & Undo',
        items: [
          'Fix history & revert — Every auto-fix snapshots the flow state before applying. Revert the last fix from the popup\'s "Fix History" section. Fix history entries are preserved beyond the normal capture limit so you can verify the fix works before cleaning up.',
          'Clear fix history — Once you\'ve confirmed a fix is correct, remove it from the history to keep the popup clean.',
          'Revert safety warning — If the flow was saved again after a fix, or if newer fixes exist on the same flow, a confirmation dialog warns about potential data loss before reverting.',
          'Dangling-output fix creates log nodes — The fix now creates a log_be/log_fe node between each unconnected Failure output and the end_failure node, with a descriptive log message identifying the source node by title.',
          'BE/FE-aware failure wiring — The fix detects whether a node is backend or frontend and wires to the correct end_failure_be/end_failure_fe node. If the matching end node doesn\'t exist, it is created automatically.',
          'Smart log node placement — New log nodes are placed in a horizontal row below all existing nodes, avoiding overlap with the current canvas layout.',
          'Fix capture suppression — After a fix or undo, captures are suppressed briefly so the page reload doesn\'t re-trigger a duplicate validation overlay.'
        ]
      },
      {
        heading: 'Warning Management',
        items: [
          'Ignore node — Dismiss a specific warning for a specific node ("this is fine for this node"). Persists across browser restarts.',
          'Mute rule — Hide all warnings from a rule for the entire flow. Session-scoped (resets on browser restart).',
          'Hidden warnings section — Collapsed section at the bottom of the overlay listing all ignored/muted warnings, with individual "Show" buttons to restore them.',
          'Side panel support — "Ignore node" and "Mute rule" buttons are available in the side panel validation report as well as the overlay.'
        ]
      },
      {
        heading: 'IO Platform 5.2/5.3 Compatibility',
        items: [
          'Dual data/meta field support — Node properties like title, label, type, is_backend, and frontend_available are now read from both node.meta and node.data, supporting both IO platform 5.2 and 5.3 field layouts.'
        ]
      },
      {
        heading: 'Bug Fixes',
        items: [
          'Admin team filtering — Fixed side panel not returning results when admins filter by teams they aren\'t a member of.',
          'Team name display — Cards now correctly show team names when the "All teams" toggle is active.',
          'Callback data false positive — Fixed invalid-callback-data rule incorrectly flagging local JS variables used in callback data objects.',
          'Dangling-output fix no longer corrupts flows — Rewrote the fix to use the exact minimal node structure accepted by the IO platform, preventing connection wipes.'
        ]
      }
    ]
  },
  {
    version: '1.1.0',
    date: 'Mar 5, 2026',
    sections: [
      {
        heading: 'New Features',
        items: [
          'Mute warnings per flow — Mute individual validation rules for a specific flow via the overlay. Muted rules are hidden from the badge count and overlay, with a summary showing how many are muted. Unmute all from the overlay.',
          'Nested flow required-variable check — New critical-severity rule that detects when a nested/library flow uses variables that aren\'t set internally, meaning the parent flow must provide them.'
        ]
      },
      {
        heading: 'Validation Improvements',
        items: [
          'Vuetify form validation — auto-fixable rule ensuring all form inputs are included in keys_to_store.',
          'CSS syntax checking extended to head_elements, temporary_head_elements, and template style blocks, including bare CSS declaration detection.',
          'fe-head-unwrapped extended to check head_elements arrays for unwrapped CSS/JS.',
          'Nested flow validation rules: Failure output must be connected, and decision outputs must match library\'s custom ends.',
          'Fixed library flow reachability (nested_start recognized as entry point).',
          'Silenced validation on empty flows (new flow creation).'
        ]
      },
      {
        heading: 'Bug Fixes & Stability',
        items: [
          'Replaced chrome.runtime.sendMessage calls in content script with safe wrapper — prevents errors when the extension is reloaded while a page is open.',
          'Removed unused scripting permission (fixed Chrome Web Store rejection).'
        ]
      }
    ]
  },
  {
    version: '1.0.0',
    date: 'Feb 22, 2026',
    sections: [
      {
        items: ['Initial Chrome Web Store release.']
      },
      {
        heading: 'Real-time Flow Validation',
        items: [
          'Automatically intercepts Identity Orchestration workflow save requests and validates them in real-time.',
          'Severity-colored badge on the extension icon showing warning/error counts.',
          'In-page overlay displaying validation results grouped by rule, with one-click navigation to problem nodes.',
          'Per-rule auto-fix with save detection — fix issues and re-save without leaving the page.',
          'Issue reporting — submit a report with screenshot directly from the overlay.'
        ]
      },
      {
        heading: 'Validation Rules (23 rules)',
        items: [
          'Orphan nodes — detects nodes not connected to any path from start.',
          'Broken references — outputs pointing to non-existent nodes.',
          'Name/path mismatch — node name doesn\'t match its path in the flow tree.',
          'Missing end node fields — end nodes lacking required configuration.',
          'Start node missing outputs — start node with no outgoing connections.',
          'Dangling outputs — connected outputs leading nowhere.',
          'Invalid mapping value — mapping attributes with invalid values.',
          'Mapping not saved to store — computed mappings missing from keys_to_store.',
          'Undefined template variables — {{x.y}} used without a prior setter in the execution path.',
          'Invalid callback data — script callback values must be valid literals or template variables.',
          'Unquoted template in script — template variables in scripts that need quoting.',
          'Script returns invalid output — script node returning an unexpected output name.',
          'Incorrect callback return — wrong callback return pattern in scripts.',
          'Script tags in FE form — disallowed <script> tags in frontend form HTML.',
          'Unnecessary DOMContentLoaded — redundant event listener in FE form scripts.',
          'Missing preventDefault — non-Vuetify forms missing form submit prevention.',
          'Script references non-existent element — JS referencing HTML IDs that don\'t exist.',
          'Unwrapped content in FE head — CSS/JS in head fields without proper <style>/<script> tags.',
          'Start template missing monokeeForm — start node template missing the required form wrapper.',
          'HTML syntax errors — malformed HTML in node templates.',
          'CSS syntax errors — invalid CSS in stylesheets and inline styles.',
          'JavaScript syntax errors — JS parse errors in script nodes and FE forms.',
          'Variable not available to frontend — FE node using a variable marked as backend-only.'
        ]
      },
      {
        heading: 'Marketplace Side Panel',
        items: [
          'Browse IO Marketplace nodes directly from the extension side panel.',
          'Search, filter by tags and teams, and copy node JSON with one click.',
          'Pin and reorder favorite nodes for quick access.',
          'Dynamic card layout with pagination.'
        ]
      },
      {
        heading: 'Marketplace Integration',
        items: [
          'One-click sign in — connect to your IO Marketplace account from the extension popup.',
          'API key management for persistent authentication.',
          'Nested flow resolution — automatically fetches library flows to validate cross-flow variable usage.'
        ]
      }
    ]
  }
];

interface RuleChangelogEntry {
  version: string;
  date: string;
  items: string[];
}

export function ExtensionChangelogPage() {
  const [storeVersion, setStoreVersion] = useState<string | null>(FALLBACK_STORE_VERSION);
  const [storeStatus, setStoreStatus] = useState<string | null>(FALLBACK_STORE_STATUS);
  const [ruleUpdates, setRuleUpdates] = useState<RuleChangelogEntry[]>([]);

  useEffect(() => {
    api.getExtensionStoreVersion()
      .then(r => { if (r.version) { setStoreVersion(r.version); setStoreStatus(r.status); } })
      .catch(() => {});
    api.getRuleChangelog()
      .then(r => setRuleUpdates(r.entries || []))
      .catch(() => {});
  }, []);

  // storeVersion = version from CWS developer dashboard (draft projection)
  // storeStatus = publish status (PUBLISHED, UNLISTED, DRAFT, or null)
  const isPublished = storeStatus === 'PUBLISHED' || storeStatus === 'UNLISTED';

  const liveVersion = storeVersion
    ? isPublished
      ? storeVersion
      : changelog
          .filter((e) => compareVersions(e.version, storeVersion) < 0 && !e.skipped)
          .sort((a, b) => compareVersions(b.version, a.version))[0]?.version ?? null
    : null;

  return (
    <div className="privacy">
      <h1>IO Inspector Changelog</h1>
      <p className="privacy__updated">
        <a
          href="https://chromewebstore.google.com/detail/io-inspector/kkbgajpjbbhlfjjkfcfoedmadoeoebnk"
          target="_blank"
          rel="noopener noreferrer"
        >
          View on Chrome Web Store
        </a>
      </p>

      <div className="changelog-columns">
        <div className="changelog-col changelog-col--main">
          {changelog.map((entry) => {
            const cmp = storeVersion ? compareVersions(entry.version, storeVersion) : 0;
            const isLive = entry.version === liveVersion;
            const isPending = cmp === 0 && !isPublished;
            const isUpcoming = cmp > 0;
            return (
              <section key={entry.version} className={`changelog-version${entry.skipped ? ' changelog-skipped' : isUpcoming ? ' changelog-upcoming' : isPending ? ' changelog-pending' : ''}`}>
                <h2>
                  v{entry.version} — {entry.date}
                  {entry.skipped && <span className="changelog-badge changelog-badge--skipped">Skipped — included in next release</span>}
                  {!entry.skipped && isLive && <span className="changelog-badge changelog-badge--live">Live</span>}
                  {!entry.skipped && isPending && <span className="changelog-badge changelog-badge--pending">Pending store approval</span>}
                  {!entry.skipped && isUpcoming && <span className="changelog-badge changelog-badge--upcoming">Coming soon</span>}
                </h2>
                {entry.sections.map((section, i) => (
                  <div key={i}>
                    {section.heading && <h3>{section.heading}</h3>}
                    <ul>
                      {section.items.map((item, j) => (
                        <li key={j}>{item}</li>
                      ))}
                    </ul>
                  </div>
                ))}
              </section>
            );
          })}
        </div>

        {ruleUpdates.length > 0 && (
          <div className="changelog-col changelog-col--rules">
            <h2 className="rule-updates-heading">Validation Rule Updates</h2>
            <p className="rule-updates-subtitle">
              Server-side updates — take effect automatically, no extension update required.
            </p>
            {ruleUpdates.map((entry, i) => (
              <section key={i} className="changelog-version changelog-rule-update">
                <h2>
                  Rules {entry.version} — {entry.date}
                  <span className="changelog-badge changelog-badge--rules">Server-side</span>
                </h2>
                <ul>
                  {entry.items.map((item, j) => (
                    <li key={j}>{item}</li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
